import {
  ComponentRef,
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  input,
  inputBinding,
  isDevMode,
  outputBinding,
  signal,
  Signal,
  ViewContainerRef,
  WritableSignal
} from '@angular/core';
import {
  DEFAULT_POPOVER_CLOSE_LABEL,
  DEFAULT_POPOVER_PLACEMENT,
  POPOVER_ANCHOR_GAP,
  POPOVER_VIEWPORT_MARGIN,
  PopoverPlacement
} from './index';
import { PopoverPanel } from './popover-panel';

let sequence = 0;

/**
 * Attaches a native popover to a `<button>`.
 *
 * ```html
 * <button type="button" okePopover="Rates update <em>nightly</em>." popoverPlacement="bottom-left">
 *   Rates
 * </button>
 * ```
 *
 * The panel is created as the adjacent sibling `<div popover="auto">` of the host and is toggled
 * declaratively via `popovertarget`, so Esc and light-dismiss come from the platform.
 */
@Directive({
  selector: '[okePopover]',
  host: {
    '[style.cursor]': '"pointer"',
    '[style.anchor-name]': 'anchorName',
    '[attr.popovertarget]': 'panelId',
    '[attr.popovertargetaction]': '"toggle"',
    '[attr.aria-expanded]': '$isOpen()'
  }
})
export class Popover {
  private readonly _anchor: HTMLElement = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly _viewContainerRef = inject(ViewContainerRef);
  private readonly _destroyRef = inject(DestroyRef);
  /** Manual positioning is only needed where CSS anchor positioning is unsupported. */
  private readonly _shouldPositionManually = !hasAnchorPositioningSupport();
  private readonly _$isOpen: WritableSignal<boolean> = signal(false);
  protected readonly panelId = `oke-popover-${++sequence}`;
  protected readonly anchorName = `--${this.panelId}`;
  protected readonly $isOpen: Signal<boolean> = this._$isOpen.asReadonly();
  /** Plain string. Inline text-level elements (`<em>`, `<strong>`, `<code>`, …) are allowed; anything else is unwrapped. */
  readonly $content = input.required<string>({ alias: 'okePopover' });
  readonly $placement = input<PopoverPlacement>(DEFAULT_POPOVER_PLACEMENT, { alias: 'popoverPlacement' });
  readonly $closeLabel = input<string>(DEFAULT_POPOVER_CLOSE_LABEL, { alias: 'popoverCloseLabel' });
  private readonly _panelRef: ComponentRef<PopoverPanel> = this._viewContainerRef.createComponent(PopoverPanel, {
    bindings: [
      inputBinding('id', () => this.panelId),
      inputBinding('content', () => this.$content()),
      inputBinding('placement', () => this.$placement()),
      inputBinding('closeLabel', () => this.$closeLabel()),
      inputBinding('anchorName', () => (this._shouldPositionManually ? null : this.anchorName)),
      outputBinding<boolean>('toggled', isOpen => this._onToggle(isOpen))
    ]
  });
  private readonly _panel: HTMLElement = this._panelRef.location.nativeElement;
  private readonly _reposition = (): void => this._position();

  constructor() {
    if (isDevMode() && !isPopoverInvoker(this._anchor)) {
      console.warn(
        `[okePopover] must be applied to a <button> (or <input type="button|submit|reset|image">) ` +
          `because invoking a popover with "popovertarget" is only supported on buttons. ` +
          `Received <${this._anchor.tagName.toLowerCase()}>; the popover will not open.`
      );
    }
    this._destroyRef.onDestroy(() => this._unbindReflow());
  }

  private _onToggle(isOpen: boolean): void {
    this._$isOpen.set(isOpen);
    if (!this._shouldPositionManually) {
      return;
    }
    if (isOpen) {
      this._position();
      window.addEventListener('scroll', this._reposition, { capture: true, passive: true });
      window.addEventListener('resize', this._reposition, { passive: true });
    } else {
      this._unbindReflow();
    }
  }

  private _unbindReflow(): void {
    window.removeEventListener('scroll', this._reposition, { capture: true });
    window.removeEventListener('resize', this._reposition);
  }

  private _position(): void {
    const { top, left } = computePosition(
      this._anchor.getBoundingClientRect(),
      this._panel.getBoundingClientRect(),
      this.$placement(),
      { width: window.innerWidth, height: window.innerHeight }
    );
    this._panel.style.top = `${top}px`;
    this._panel.style.left = `${left}px`;
  }
}

function hasAnchorPositioningSupport(): boolean {
  return (
    typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('anchor-name', '--oke-popover')
  );
}

function isPopoverInvoker(element: HTMLElement): boolean {
  return (
    element instanceof HTMLButtonElement ||
    (element instanceof HTMLInputElement && ['button', 'submit', 'reset', 'image'].includes(element.type))
  );
}

/** Viewport coordinates for the panel, clamped so it never renders off-screen. */
function computePosition(
  anchor: DOMRect,
  panel: DOMRect,
  placement: PopoverPlacement,
  viewport: { width: number; height: number }
): { top: number; left: number } {
  let top: number;
  if (placement.startsWith('top-')) {
    top = anchor.top - panel.height - POPOVER_ANCHOR_GAP;
  } else if (placement.startsWith('bottom-')) {
    top = anchor.bottom + POPOVER_ANCHOR_GAP;
  } else {
    top = anchor.top + (anchor.height - panel.height) / 2;
  }

  let left: number;
  if (placement === 'center-left') {
    left = anchor.left - panel.width - POPOVER_ANCHOR_GAP;
  } else if (placement === 'center-right') {
    left = anchor.right + POPOVER_ANCHOR_GAP;
  } else if (placement.endsWith('-left')) {
    left = anchor.left;
  } else if (placement.endsWith('-right')) {
    left = anchor.right - panel.width;
  } else {
    left = anchor.left + (anchor.width - panel.width) / 2;
  }

  return {
    top: clamp(top, POPOVER_VIEWPORT_MARGIN, viewport.height - panel.height - POPOVER_VIEWPORT_MARGIN),
    left: clamp(left, POPOVER_VIEWPORT_MARGIN, viewport.width - panel.width - POPOVER_VIEWPORT_MARGIN)
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}
